import React from "react";
import { observer } from "mobx-react";

const RenderRow = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value) && value.length === 0) return null;
  return (
    <tr>
      <td>{label}</td>
      <td>{Array.isArray(value) ? value.join(", ") : (typeof value === "object" ? JSON.stringify(value) : value)}</td>
    </tr>
  );
};

const formatKey = (key) => key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const formatHQ = (company) => {
  if (!company) return null;
  if (company.offices && company.offices[0]) {
    const o = company.offices[0];
    return [o.city, o.state, o.country].filter(Boolean).join(", ");
  }
  return [company.city, company.state, company.country].filter(Boolean).join(", ") || null;
};

const CompanyOverview = observer(({ companyData, companyInsights, deepDiveIntel }) => {
  // Real shape:
  //   companyData.company_data → { name, founded, industry[], revenue, followers, description, offices[], specialities[], website, ... }
  //   companyData.web_data → news items (object or array)
  //   companyInsights.insights → array of grouped insight items
  //   companyInsights.data → signals (rendered separately by SellingSignals, ignore here)
  //   deepDiveIntel → competitor_names[], tools_in_stack[], revenue_data{}
  const company = companyData?.company_data || {};
  const webData = companyData?.web_data;
  const companyNews = Array.isArray(webData)
    ? webData
    : (webData && typeof webData === "object" ? (webData.data || []) : []);

  const insightsList = Array.isArray(companyInsights?.insights) ? companyInsights.insights : [];

  const companyName = company.name || "";
  if (!companyName && insightsList.length === 0 && companyNews.length === 0) return null;

  const competitors = deepDiveIntel?.competitor_names || [];
  const tools = deepDiveIntel?.tools_in_stack || [];
  const revenue = deepDiveIntel?.revenue_data || {};

  // Group news by category
  const groupedNews = companyNews.reduce((acc, item) => {
    const cat = item?.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Group static insights by category too (insights array often has category-tagged items)
  const groupedInsights = insightsList.reduce((acc, item) => {
    const cat = item?.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <>
      {/* Company Overview Table */}
      <div className="drSection">
        <div className="drSectionHeader">
          <span className="drSectionIcon">🏢</span>
          <h3>{companyName} Overview</h3>
        </div>
        {company.description && (
          <p style={{ fontSize: "13px", color: "#555", marginBottom: "12px", lineHeight: 1.5 }}>
            {company.description}
          </p>
        )}
        <table className="drTable">
          <thead><tr><th>Attribute</th><th>Details</th></tr></thead>
          <tbody>
            <RenderRow label="Founded" value={company.founded} />
            <RenderRow label="Headquarters" value={formatHQ(company)} />
            <RenderRow label="Team Size" value={company.team_size || company.staff?.total} />
            <RenderRow label="Industry" value={company.industry} />
            <RenderRow label="Revenue" value={company.revenue} />
            {revenue.arr && <tr><td>ARR</td><td>{revenue.arr}</td></tr>}
            <RenderRow label="LinkedIn Followers" value={company.followers} />
            <RenderRow label="Website" value={company.website} />
            <RenderRow label="Phone" value={company.phone} />
            <RenderRow label="Specialities" value={company.specialities} />
          </tbody>
        </table>

        {/* Competitors & Tech Stack from deep-dive intel */}
        {(competitors.length > 0 || tools.length > 0) && (
          <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
            {competitors.length > 0 && (
              <div>
                <span style={{ fontSize: "11px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.3px" }}>Known Competitors</span>
                <div className="drTagList" style={{ marginTop: "4px" }}>
                  {competitors.map((c, i) => <span key={i} className="drTag">{c}</span>)}
                </div>
              </div>
            )}
            {tools.length > 0 && (
              <div>
                <span style={{ fontSize: "11px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.3px" }}>Tools in Stack</span>
                <div className="drTagList" style={{ marginTop: "4px" }}>
                  {tools.map((t, i) => <span key={i} className="drTag drTagTech">{t}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Company Insights (from companyInsights.insights[]) */}
      {Object.keys(groupedInsights).length > 0 && (
        <div className="drSection">
          <div className="drSectionHeader">
            <span className="drSectionIcon">💡</span>
            <h3>{companyName} Insights</h3>
          </div>
          <div className="drNewsList">
            {Object.entries(groupedInsights).map(([category, items]) => (
              <div key={category} className="drNewsCategory">
                <h4 className="drNewsCategoryTitle">🔹 {formatKey(category)}</h4>
                {items.map((item, idx) => (
                  <div key={idx} className="drNewsItem">
                    <p className="drNewsText">
                      {item.date && <span className="drNewsDate">{item.date}: </span>}
                      {item.insight || item.insights || item.text}
                    </p>
                    {item.associated_links?.length > 0 && (
                      <div className="drNewsSources">
                        {item.associated_links.map((link, li) => {
                          const domain = (() => { try { return new URL(link).hostname; } catch { return ""; } })();
                          return (
                            <a key={li} href={link} target="_blank" rel="noopener noreferrer" className="drNewsSourceLink">
                              <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} alt="" />
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company News (from companyData.web_data) */}
      {companyNews.length > 0 && (
        <div className="drSection">
          <div className="drSectionHeader">
            <span className="drSectionIcon">📰</span>
            <h3>{companyName} News</h3>
          </div>
          <div className="drNewsList">
            {Object.entries(groupedNews).map(([category, items]) => (
              <div key={category} className="drNewsCategory">
                <h4 className="drNewsCategoryTitle">🔹 {formatKey(category)}</h4>
                {items.map((item, idx) => (
                  <div key={idx} className="drNewsItem">
                    <p className="drNewsText">
                      {item.date && <span className="drNewsDate">{item.date}: </span>}
                      {item.insight || item.insights || item.text || item.title}
                    </p>
                    {item.associated_links?.length > 0 && (
                      <div className="drNewsSources">
                        {item.associated_links.map((link, li) => {
                          const domain = (() => { try { return new URL(link).hostname; } catch { return ""; } })();
                          return (
                            <a key={li} href={link} target="_blank" rel="noopener noreferrer" className="drNewsSourceLink">
                              <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} alt="" />
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
});

export default CompanyOverview;
